import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Checkbox, CircularProgress, IconButton, SvgIcon } from "@mui/material";

import "./style.scss";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { ProvidersContainer } from "../../../common/containers/providers/providers.container";
import { ToastContainer } from "../../../components/toast/toast.comp";
import { ReactComponent as CheckboxIcon } from "../../../assets/checkbox.icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../../assets/checkbox-checked.icon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/trash.icon.svg";
import { ReactComponent as RefreshIcon } from "../../../assets/refresh.icon.svg";
import { ReactComponent as CheckCircleIcon } from "../../../assets/check-circle.icon.svg";
import { ReactComponent as CloseIcon } from "../../../assets/cross.icon.svg";
import { ReactComponent as SearchIcon } from "../../../assets/search.icon.svg";
import EmptyLeftMenuLayout from "../../../components/layout/empty-left-menu-layout.comp";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";
import {
    ActionMenu,
    CheckboxMenuFilterT,
    SelectionFilter
} from "../../../components/menu-filter/menu-filter.comp";
import {
    addCrawlExclusion,
    approveCrawlRun,
    discardCrawlRun,
    fetchCrawlItems,
    fetchCrawlRun,
    refreshContentItemProvider,
    setCrawlDecisions
} from "../../../api/admin/content-providers/endpoints-referrers";
import {
    CrawlItem,
    CrawlItemKind,
    CrawlOutcome,
    CrawlRun
} from "../../../common/interfaces/admin/crawl.interface";
import { MenuItem } from "../../../common/interfaces/comps/menu-item.interface";
import CrawlPreviewDrawer from "./crawl-preview-drawer.comp";
import ExcludeByPatternDialog from "./exclude-by-pattern-dialog.comp";
import {
    CRAWL_SORTS,
    CRAWL_SORT_LIST,
    DECISION_LABELS,
    DEFAULT_CRAWL_SORT,
    KIND_LABELS,
    OUTCOME_LABELS,
    filterParam,
    formatBytes,
    formatDate,
    incompleteSummary,
    itemSize,
    kindLabel,
    failureLabel,
    reasonLabel,
    reviewReasonLabel
} from "./review-crawl.util";

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 1000;

/** The checked entries re-pointed at the current menu objects (same value), unchanged when nothing moved. */
const remapChecked = <T,>(checked: MenuItem<T>[], items: MenuItem<T>[]): MenuItem<T>[] => {
    const next = checked
        .map(entry => items.find(item => item.value === entry.value))
        .filter((item): item is MenuItem<T> => !!item);
    const same = next.length === checked.length && next.every((item, i) => item === checked[i]);
    return same ? checked : next;
};

/**
 * Crawl review (web_scraper_provider_design.md D16, §10): the inventory of a
 * crawl that is awaiting review, previews from the staged snapshots, include /
 * exclude decisions, pattern exclusions, the removals the admin keeps or
 * applies, and Approve / Discard / Discard and re-crawl.
 */
const ReviewCrawlView = () => {
    const { providerId, runId } = useParams();
    const navigate = useNavigate();
    const { user } = FirebaseAuthContainer.useContainer();
    const { providers, loadProviders } = ProvidersContainer.useContainer();
    const { showToast } = ToastContainer.useContainer();

    const companyId = user?.companyId;
    const providerIdNumber = Number(providerId);
    const provider = providers.find(p => p.id === providerIdNumber);

    const [run, setRun] = useState<CrawlRun | null>(null);
    const [runError, setRunError] = useState("");
    const [items, setItems] = useState<CrawlItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loadingItems, setLoadingItems] = useState(false);
    // Search, filters and sort follow the files list (knowledge-base-elements):
    // the search box is debounced, the filters are checkbox menus holding
    // MenuItem references, and the sort is a selection menu.
    const [searchString, setSearchString] = useState("");
    const [searchParam, setSearchParam] = useState("");
    const [checkedKinds, setCheckedKinds] = useState<MenuItem<CrawlItemKind>[]>([]);
    const [checkedOutcomes, setCheckedOutcomes] = useState<MenuItem<CrawlOutcome>[]>([]);
    const [checkedReasons, setCheckedReasons] = useState<MenuItem<string>[]>([]);
    const [checkedDecisions, setCheckedDecisions] = useState<MenuItem<"include" | "exclude">[]>([]);
    const [selectedSort, setSelectedSort] = useState<string>(DEFAULT_CRAWL_SORT);
    const [reasonFacets, setReasonFacets] = useState<{ reason: string; count: number }[]>([]);
    const [checkedIds, setCheckedIds] = useState<string[]>([]);
    const [removals, setRemovals] = useState<CrawlItem[]>([]);
    const [includedTotal, setIncludedTotal] = useState(0);
    const [includedRemovals, setIncludedRemovals] = useState(0);
    const [previewItem, setPreviewItem] = useState<CrawlItem | null>(null);
    const [exclusionOpen, setExclusionOpen] = useState(false);
    const [confirm, setConfirm] = useState<"approve" | "discard" | "recrawl" | null>(null);
    const [busy, setBusy] = useState(false);

    const reviewable = run?.status === "awaiting_review";

    const loadRun = useCallback(() => {
        if (!companyId || !runId || Number.isNaN(providerIdNumber)) return;
        fetchCrawlRun(companyId, providerIdNumber, runId)
            .then(response => setRun(response.data))
            .catch(() =>
                setRunError("This crawl could not be loaded. It may have been discarded.")
            );
    }, [companyId, providerIdNumber, runId]);

    const loadTotals = useCallback(() => {
        if (!companyId || !runId || Number.isNaN(providerIdNumber)) return;
        fetchCrawlItems(companyId, providerIdNumber, runId, { decision: "include", pageSize: 1 })
            .then(response => setIncludedTotal(response.data.total))
            .catch(() => undefined);
        fetchCrawlItems(companyId, providerIdNumber, runId, {
            decision: "include",
            outcome: "removed",
            pageSize: 1
        })
            .then(response => setIncludedRemovals(response.data.total))
            .catch(() => undefined);
    }, [companyId, providerIdNumber, runId]);

    const loadRemovals = useCallback(() => {
        if (!companyId || !runId || Number.isNaN(providerIdNumber)) return;
        fetchCrawlItems(companyId, providerIdNumber, runId, { outcome: "removed", pageSize: 500 })
            .then(response => setRemovals(response.data.items))
            .catch(() => undefined);
    }, [companyId, providerIdNumber, runId]);

    const loadItems = useCallback(
        (nextPage: number, append: boolean) => {
            if (!companyId || !runId || Number.isNaN(providerIdNumber)) return;
            setLoadingItems(true);
            fetchCrawlItems(companyId, providerIdNumber, runId, {
                kind: filterParam(checkedKinds),
                outcome: filterParam(checkedOutcomes),
                reason: filterParam(checkedReasons),
                decision: filterParam(checkedDecisions),
                q: searchParam || undefined,
                ...(CRAWL_SORTS[selectedSort] ?? CRAWL_SORTS[DEFAULT_CRAWL_SORT]),
                page: nextPage,
                pageSize: PAGE_SIZE
            })
                .then(response => {
                    setTotal(response.data.total);
                    setPage(nextPage);
                    setItems(prev =>
                        append ? [...prev, ...response.data.items] : response.data.items
                    );
                    // Keep the facet array stable when nothing changed: the
                    // Reason menu items are derived from it and the checkbox
                    // filter matches checked entries by reference.
                    const facets = response.data.reasons;
                    if (facets) {
                        setReasonFacets(prev =>
                            JSON.stringify(prev) === JSON.stringify(facets) ? prev : facets
                        );
                    }
                })
                .catch(() => showToast("The inventory could not be loaded"))
                .finally(() => setLoadingItems(false));
        },
        [
            companyId,
            providerIdNumber,
            runId,
            checkedKinds,
            checkedOutcomes,
            checkedReasons,
            checkedDecisions,
            searchParam,
            selectedSort,
            showToast
        ]
    );

    // The search runs by itself once typing pauses, as on the files list.
    useEffect(() => {
        const debouncer = setTimeout(() => setSearchParam(searchString.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(debouncer);
    }, [searchString]);

    const handleSearchChange = (evt: ChangeEvent<HTMLInputElement>) =>
        setSearchString(evt.target?.value ?? "");
    const clearSearchString = () => setSearchString("");

    useEffect(() => {
        loadRun();
        loadTotals();
        loadRemovals();
    }, [loadRun, loadTotals, loadRemovals]);

    useEffect(() => {
        setCheckedIds([]);
        loadItems(1, false);
    }, [loadItems]);

    // Items are patched in place after a decision so the list keeps its
    // scroll position; ids (not objects) track the selection (DESIGN_GUIDE §4).
    const patchDecision = (ids: string[], decision: "include" | "exclude") => {
        const apply = (list: CrawlItem[]) =>
            list.map(item =>
                ids.includes(item.id)
                    ? { ...item, decision, decisionSource: "admin" as const }
                    : item
            );
        setItems(apply);
        setRemovals(apply);
        setPreviewItem(prev => (prev && ids.includes(prev.id) ? { ...prev, decision } : prev));
    };

    const decide = (ids: string[], decision: "include" | "exclude") => {
        if (!companyId || !runId || !ids.length || !reviewable) return;
        setBusy(true);
        setCrawlDecisions(companyId, providerIdNumber, runId, ids, decision)
            .then(response => {
                patchDecision(ids, decision);
                loadTotals();
                showToast(
                    `${response.data.updated} item${response.data.updated === 1 ? "" : "s"} ${
                        decision === "include" ? "included" : "excluded"
                    }`
                );
            })
            .catch(() => showToast("The decision could not be saved"))
            .finally(() => setBusy(false));
    };

    const excludeByPattern = (pattern: string, persist: boolean) => {
        if (!companyId || !runId || !reviewable) return;
        setBusy(true);
        addCrawlExclusion(companyId, providerIdNumber, runId, pattern, persist)
            .then(response => {
                setExclusionOpen(false);
                showToast(
                    `${response.data.matched} item${response.data.matched === 1 ? "" : "s"} excluded${
                        response.data.persisted ? " and the pattern saved" : ""
                    }`
                );
                loadItems(1, false);
                loadRemovals();
                loadTotals();
            })
            .catch(error => {
                const message = error?.response?.data?.message;
                showToast(
                    typeof message === "string" ? message : "The pattern could not be applied"
                );
            })
            .finally(() => setBusy(false));
    };

    const approve = () => {
        if (!companyId || !runId) return;
        setBusy(true);
        approveCrawlRun(companyId, providerIdNumber, runId)
            .then(() => {
                showToast("Approved. Pages are being indexed.");
                loadProviders();
                navigate(`/knowledge-base/${providerIdNumber}`);
            })
            .catch(error => {
                const message = error?.response?.data?.message;
                showToast(
                    typeof message === "string" ? message : "The crawl could not be approved"
                );
                loadRun();
            })
            .finally(() => setBusy(false));
    };

    const discard = (recrawl: boolean) => {
        if (!companyId || !runId || !user?.currentKnowledgeBaseId) return;
        setBusy(true);
        const action = recrawl
            ? refreshContentItemProvider(companyId, providerIdNumber, {
                  knowledgeBaseId: user.currentKnowledgeBaseId,
                  discardPending: true
              })
            : discardCrawlRun(companyId, providerIdNumber, runId);
        action
            .then(() => {
                showToast(
                    recrawl ? "Crawl discarded. A new crawl has started." : "Crawl discarded."
                );
                loadProviders();
                navigate(`/knowledge-base/${providerIdNumber}`);
            })
            .catch(error => {
                const message = error?.response?.data?.message;
                showToast(
                    typeof message === "string" ? message : "The crawl could not be discarded"
                );
                loadRun();
            })
            .finally(() => setBusy(false));
    };

    const allChecked = items.length > 0 && items.every(item => checkedIds.includes(item.id));
    const toggleAll = () => setCheckedIds(allChecked ? [] : items.map(item => item.id));
    const toggleOne = (id: string) =>
        setCheckedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

    const counts = run?.counts ?? {};
    // Menu items are memoized: the checkbox filter matches by reference.
    const kindItems = useMemo<MenuItem<CrawlItemKind>[]>(
        () => KIND_LABELS.map(({ kind, label }) => ({ label, value: kind })),
        []
    );
    const outcomeItems = useMemo<MenuItem<CrawlOutcome>[]>(() => {
        const byOutcome = (run?.counts ?? {}) as Record<string, number | undefined>;
        return OUTCOME_LABELS.map(({ outcome, label }) => ({
            label: run ? `${label} (${byOutcome[outcome] ?? 0})` : label,
            value: outcome
        }));
    }, [run]);
    const reasonItems = useMemo<MenuItem<string>[]>(
        () =>
            reasonFacets.map(({ reason, count }) => ({
                label: `${reasonLabel(reason)} (${count})`,
                value: reason
            })),
        [reasonFacets]
    );
    const decisionItems = useMemo<MenuItem<"include" | "exclude">[]>(
        () => DECISION_LABELS.map(({ decision, label }) => ({ label, value: decision })),
        []
    );
    // When a menu's items are rebuilt (new counts after a reload), point the
    // checked entries at the new objects so they stay checked and can be
    // cleared; entries whose value is gone are dropped.
    useEffect(() => {
        setCheckedReasons(prev => remapChecked(prev, reasonItems));
    }, [reasonItems]);
    useEffect(() => {
        setCheckedOutcomes(prev => remapChecked(prev, outcomeItems));
    }, [outcomeItems]);
    const host = useMemo(() => {
        try {
            return provider?.providerUrl ? new URL(provider.providerUrl).host : "";
        } catch {
            return "";
        }
    }, [provider?.providerUrl]);
    const removalsCount = counts.removed ?? 0;

    const actions = [
        {
            name: "Include selected",
            description: "Index the checked items when the crawl is approved",
            action: () => decide(checkedIds, "include")
        },
        {
            name: "Exclude selected",
            description: "Leave the checked items out of this crawl",
            action: () => decide(checkedIds, "exclude")
        },
        {
            name: "Exclude by pattern…",
            description: "Exclude every page whose address matches a pattern",
            action: () => setExclusionOpen(true)
        }
    ];

    return (
        <EmptyLeftMenuLayout>
            <HeaderLayout blurHeader={false}>
                <div className="review-crawl">
                    <div className="review-crawl__header">
                        <div className="review-crawl__heading">
                            <span className="review-crawl__title" role="heading" aria-level={1}>
                                Review crawl{provider ? `: ${provider.displayName}` : ""}
                            </span>
                            {host && <span className="review-crawl__host">{host}</span>}
                        </div>
                        <Button
                            variant="text"
                            className="thirt-button"
                            onClick={() => navigate(`/knowledge-base/${providerIdNumber}`)}
                        >
                            Back to files
                        </Button>
                    </div>

                    {runError && (
                        <Alert severity="error" className="review-crawl__banner">
                            {runError}
                        </Alert>
                    )}
                    {run && !reviewable && (
                        <Alert severity="info" className="review-crawl__banner">
                            {`This crawl is ${run.status.replace(/_/g, " ")}; nothing can be changed here.`}
                        </Alert>
                    )}
                    {run?.error && reviewable && (
                        <Alert severity="warning" className="review-crawl__banner">
                            {run.error}
                        </Alert>
                    )}

                    {run && (
                        <dl className="review-crawl__summary" aria-label="Crawl summary">
                            <div>
                                <dt>Started</dt>
                                <dd>
                                    {formatDate(run.startedAt)}
                                    {run.trigger === "scheduled"
                                        ? " (scheduled)"
                                        : run.trigger === "page"
                                          ? " (single page re-fetch)"
                                          : ""}
                                </dd>
                            </div>
                            <div>
                                <dt>Crawl</dt>
                                <dd>
                                    {run.crawlComplete
                                        ? "complete"
                                        : `did not finish${
                                              run.incompleteReasons?.length
                                                  ? `: ${incompleteSummary(run)}`
                                                  : ""
                                          }. Removals are not applied from an unfinished crawl.`}
                                </dd>
                            </div>
                            <div>
                                <dt>Found</dt>
                                <dd>
                                    {`${counts.discovered ?? 0} addresses, ${counts.pages ?? 0} pages, ${
                                        counts.documents ?? 0
                                    } documents${counts.media ? `, ${counts.media} media` : ""}, ${
                                        formatBytes(run.bytesFetched) || "0 B"
                                    } fetched`}
                                </dd>
                            </div>
                            {!!run.reviewReasons?.length && (
                                <div>
                                    <dt>Held for review</dt>
                                    <dd>{run.reviewReasons.map(reviewReasonLabel).join(", ")}</dd>
                                </div>
                            )}
                            {run.expiresAt && reviewable && (
                                <div>
                                    <dt>Review by</dt>
                                    <dd>{`${formatDate(run.expiresAt)} (discarded afterwards)`}</dd>
                                </div>
                            )}
                        </dl>
                    )}

                    <div className="review-crawl__toolbar">
                        <div className="review-crawl__toolbar-left">
                            <ActionMenu
                                label="Action"
                                actions={actions}
                                disabled={!reviewable || busy}
                            />
                        </div>
                        <div className="review-crawl__toolbar-center">
                            <div className="review-crawl__filter-group">
                                <div className="review-crawl__filter">
                                    <CheckboxMenuFilterT
                                        label="Type"
                                        list={kindItems}
                                        checkedList={checkedKinds}
                                        setCheckedList={setCheckedKinds}
                                        showCounter={true}
                                        rightPosition={true}
                                        minWidth={180}
                                    />
                                </div>
                                <div className="review-crawl__filter">
                                    <CheckboxMenuFilterT
                                        label="Outcome"
                                        list={outcomeItems}
                                        checkedList={checkedOutcomes}
                                        setCheckedList={setCheckedOutcomes}
                                        showCounter={true}
                                        rightPosition={true}
                                        minWidth={200}
                                    />
                                </div>
                                <div className="review-crawl__filter">
                                    <CheckboxMenuFilterT
                                        label="Reason"
                                        list={reasonItems}
                                        checkedList={checkedReasons}
                                        setCheckedList={setCheckedReasons}
                                        showCounter={true}
                                        rightPosition={true}
                                        minWidth={300}
                                    />
                                </div>
                                <div className="review-crawl__filter">
                                    <CheckboxMenuFilterT
                                        label="Decision"
                                        list={decisionItems}
                                        checkedList={checkedDecisions}
                                        setCheckedList={setCheckedDecisions}
                                        showCounter={true}
                                        rightPosition={true}
                                        minWidth={180}
                                    />
                                </div>
                                <div className="review-crawl__filter review-crawl__filter--sorting">
                                    <SelectionFilter
                                        list={CRAWL_SORT_LIST}
                                        selectedItem={selectedSort}
                                        setSelectedItem={setSelectedSort}
                                        rightPosition={true}
                                        minWidth={180}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="review-crawl__toolbar-right">
                            <div className="review-crawl__search">
                                <SvgIcon
                                    className="review-crawl__search-icon"
                                    component={SearchIcon}
                                    inheritViewBox
                                />
                                <input
                                    type="text"
                                    aria-label="Search by title or address"
                                    placeholder="Search"
                                    value={searchString}
                                    onChange={handleSearchChange}
                                />
                                {!!searchString && (
                                    <IconButton
                                        className="review-crawl__search-clear"
                                        aria-label="Clear search"
                                        onClick={clearSearchString}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="review-crawl__table-wrap">
                        <div className="crawl-table" role="table" aria-label="Crawl inventory">
                            <div className="crawl-table__header" role="row">
                                <div
                                    className="crawl-table__cell crawl-table__cell--select"
                                    role="columnheader"
                                >
                                    <Checkbox
                                        checked={allChecked}
                                        onChange={toggleAll}
                                        disabled={!reviewable || !items.length}
                                        icon={<CheckboxIcon />}
                                        checkedIcon={<CheckboxCheckedIcon />}
                                        inputProps={{ "aria-label": "Select all loaded items" }}
                                    />
                                </div>
                                <div
                                    className="crawl-table__cell crawl-table__cell--kind"
                                    role="columnheader"
                                >
                                    Type
                                </div>
                                <div
                                    className="crawl-table__cell crawl-table__cell--title"
                                    role="columnheader"
                                >
                                    Title
                                </div>
                                <div
                                    className="crawl-table__cell crawl-table__cell--url"
                                    role="columnheader"
                                >
                                    Address
                                </div>
                                <div
                                    className="crawl-table__cell crawl-table__cell--size"
                                    role="columnheader"
                                >
                                    Size
                                </div>
                                <div
                                    className="crawl-table__cell crawl-table__cell--outcome"
                                    role="columnheader"
                                >
                                    Outcome
                                </div>
                                <div
                                    className="crawl-table__cell crawl-table__cell--reason"
                                    role="columnheader"
                                >
                                    Reason
                                </div>
                                <div
                                    className="crawl-table__cell crawl-table__cell--decision"
                                    role="columnheader"
                                >
                                    Decision
                                </div>
                            </div>
                            <div className="crawl-table__body" role="rowgroup">
                                {items.map(item => (
                                    <div
                                        key={item.id}
                                        className={`crawl-table__row${
                                            item.decision === "exclude"
                                                ? " crawl-table__row--excluded"
                                                : ""
                                        }`}
                                        role="row"
                                    >
                                        <div
                                            className="crawl-table__cell crawl-table__cell--select"
                                            role="cell"
                                        >
                                            <Checkbox
                                                checked={checkedIds.includes(item.id)}
                                                onChange={() => toggleOne(item.id)}
                                                disabled={!reviewable}
                                                icon={<CheckboxIcon />}
                                                checkedIcon={<CheckboxCheckedIcon />}
                                                inputProps={{
                                                    "aria-label": `Select ${item.title || item.canonicalUrl}`
                                                }}
                                            />
                                        </div>
                                        <div
                                            className="crawl-table__cell crawl-table__cell--kind"
                                            role="cell"
                                        >
                                            <span className={`crawl-kind crawl-kind--${item.kind}`}>
                                                {kindLabel(item.kind)}
                                            </span>
                                        </div>
                                        <div
                                            className="crawl-table__cell crawl-table__cell--title"
                                            role="rowheader"
                                        >
                                            <button
                                                type="button"
                                                className="crawl-table__preview-button"
                                                onClick={() => setPreviewItem(item)}
                                                aria-label={`Preview ${item.title || item.name || item.canonicalUrl}`}
                                            >
                                                {item.title || item.name || "(untitled)"}
                                            </button>
                                        </div>
                                        <div
                                            className="crawl-table__cell crawl-table__cell--url"
                                            role="cell"
                                        >
                                            <a
                                                href={item.canonicalUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={item.canonicalUrl}
                                            >
                                                {item.canonicalUrl}
                                            </a>
                                        </div>
                                        <div
                                            className="crawl-table__cell crawl-table__cell--size"
                                            role="cell"
                                        >
                                            {itemSize(item)}
                                        </div>
                                        <div
                                            className="crawl-table__cell crawl-table__cell--outcome"
                                            role="cell"
                                        >
                                            <span
                                                className={`crawl-outcome crawl-outcome--${item.outcome}`}
                                            >
                                                {item.outcome}
                                            </span>
                                        </div>
                                        <div
                                            className="crawl-table__cell crawl-table__cell--reason"
                                            role="cell"
                                            title={[failureLabel(item), item.note, item.error]
                                                .filter(Boolean)
                                                .join(" · ")}
                                        >
                                            {[failureLabel(item), item.note]
                                                .filter(Boolean)
                                                .join(" · ")}
                                        </div>
                                        <div
                                            className="crawl-table__cell crawl-table__cell--decision"
                                            role="cell"
                                        >
                                            <span
                                                className={`crawl-decision crawl-decision--${item.decision}${
                                                    item.decisionSource !== "auto"
                                                        ? " crawl-decision--manual"
                                                        : ""
                                                }`}
                                            >
                                                {item.decision === "include"
                                                    ? "Included"
                                                    : "Excluded"}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                {!loadingItems && !items.length && (
                                    <div
                                        className="crawl-table__row crawl-table__row--empty"
                                        role="row"
                                    >
                                        <div className="crawl-table__cell" role="cell">
                                            No items match this filter.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="review-crawl__paging" role="status">
                            {loadingItems ? (
                                <>
                                    <CircularProgress size={18} /> Loading…
                                </>
                            ) : (
                                `Showing ${items.length} of ${total}`
                            )}
                            {!loadingItems && items.length < total && (
                                <Button
                                    variant="text"
                                    className="thirt-button"
                                    onClick={() => loadItems(page + 1, true)}
                                >
                                    Load more
                                </Button>
                            )}
                        </div>
                    </div>

                    {removalsCount > 0 && (
                        <section
                            className="review-crawl__removals"
                            aria-labelledby="review-crawl-removals-heading"
                        >
                            <span
                                id="review-crawl-removals-heading"
                                className="review-crawl__section-title"
                                role="heading"
                                aria-level={2}
                            >
                                {`Removals (${removalsCount})`}
                            </span>
                            <p className="review-crawl__section-hint">
                                These pages were indexed before but were not found in this crawl.
                                Apply removes them from the Assistant; Keep leaves them as they are.
                                {!run?.crawlComplete &&
                                    " The crawl did not finish, so removals stay kept."}
                            </p>
                            <ul className="review-crawl__removal-list">
                                {removals.map(item => (
                                    <li key={item.id} className="review-crawl__removal">
                                        <span className="review-crawl__removal-url">
                                            <a
                                                href={item.canonicalUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {item.title || item.canonicalUrl}
                                            </a>
                                            <span className="review-crawl__removal-reason">
                                                {reasonLabel(item.reason) ||
                                                    "Not found in this crawl"}
                                            </span>
                                        </span>
                                        <span
                                            className="review-crawl__removal-actions"
                                            role="group"
                                            aria-label={`Removal of ${item.title || item.canonicalUrl}`}
                                        >
                                            <Button
                                                variant="text"
                                                className={
                                                    item.decision === "exclude"
                                                        ? "primary-button"
                                                        : "thirt-button"
                                                }
                                                aria-pressed={item.decision === "exclude"}
                                                disabled={!reviewable || busy}
                                                onClick={() => decide([item.id], "exclude")}
                                            >
                                                Keep
                                            </Button>
                                            <Button
                                                variant="text"
                                                className={
                                                    item.decision === "include"
                                                        ? "primary-button"
                                                        : "thirt-button"
                                                }
                                                aria-pressed={item.decision === "include"}
                                                disabled={
                                                    !reviewable || busy || !run?.crawlComplete
                                                }
                                                onClick={() => decide([item.id], "include")}
                                            >
                                                Apply
                                            </Button>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <div className="review-crawl__footer">
                        <Button
                            variant="text"
                            className="thirt-button"
                            disabled={!reviewable || busy}
                            onClick={() => setConfirm("discard")}
                        >
                            Discard
                        </Button>
                        <Button
                            variant="text"
                            className="thirt-button"
                            disabled={!reviewable || busy}
                            onClick={() => setConfirm("recrawl")}
                        >
                            Discard and re-crawl
                        </Button>
                        <Button
                            variant="text"
                            className="primary-button"
                            disabled={!reviewable || busy}
                            onClick={() => setConfirm("approve")}
                        >
                            {`Approve and index (${Math.max(includedTotal - includedRemovals, 0)} item${
                                includedTotal - includedRemovals === 1 ? "" : "s"
                            }, ${includedRemovals} removal${includedRemovals === 1 ? "" : "s"})`}
                        </Button>
                    </div>
                </div>
            </HeaderLayout>

            {companyId && runId && (
                <CrawlPreviewDrawer
                    companyId={companyId}
                    providerId={providerIdNumber}
                    runId={runId}
                    item={previewItem}
                    canDecide={!!reviewable}
                    busy={busy}
                    handleClose={() => setPreviewItem(null)}
                    handleDecision={(item, decision) => decide([item.id], decision)}
                />
            )}
            {exclusionOpen && (
                <ExcludeByPatternDialog
                    open={exclusionOpen}
                    urls={items
                        .filter(item => item.decision === "include")
                        .map(item => item.canonicalUrl)}
                    totalLoaded={items.filter(item => item.decision === "include").length}
                    busy={busy}
                    handleClose={() => setExclusionOpen(false)}
                    handleApply={excludeByPattern}
                />
            )}
            <ConfirmationPopup
                open={confirm === "approve"}
                icon={CheckCircleIcon}
                title="Approve and index?"
                description={`${Math.max(includedTotal - includedRemovals, 0)} item(s) will be indexed and ${includedRemovals} removal(s) applied. Excluded items are left out.`}
                actionName="Approve"
                showCancelButton
                handleAction={() => {
                    setConfirm(null);
                    approve();
                }}
                handleClose={() => setConfirm(null)}
            />
            <ConfirmationPopup
                open={confirm === "discard"}
                type={ConfirmationPopupType.dangerous}
                icon={TrashIcon}
                title="Discard this crawl?"
                description="Everything found in this crawl is deleted and nothing is indexed. This cannot be undone; the site stays as it was."
                actionName="Discard"
                showCancelButton
                handleAction={() => {
                    setConfirm(null);
                    discard(false);
                }}
                handleClose={() => setConfirm(null)}
            />
            <ConfirmationPopup
                open={confirm === "recrawl"}
                type={ConfirmationPopupType.dangerous}
                icon={RefreshIcon}
                title="Discard and crawl again?"
                description="Everything found in this crawl is deleted and a new crawl starts right away. Your include and exclude decisions here are lost; saved patterns are kept."
                actionName="Discard and re-crawl"
                showCancelButton
                handleAction={() => {
                    setConfirm(null);
                    discard(true);
                }}
                handleClose={() => setConfirm(null)}
            />
        </EmptyLeftMenuLayout>
    );
};

export default ReviewCrawlView;
